/**
 * UI Controls for 3D Printing Configurator
 * Handles UI interactions, form controls, and display updates
 */

const materialCosts = {
  pla: 0.14,
  'pla-matte': 0.16,
  petg: 0.12,
  abs: 0.12
};

const colorOptions = {
  pla: [
    { hex: '#2A2A2A', name: 'Black', handle: 'pla-black' },
    { hex: '#9B9B9B', name: 'Grey', handle: 'pla-grey' },
    { hex: '#FAFAFA', name: 'White', handle: 'pla-white' },
    { hex: '#FF3232', name: 'Red', handle: 'pla-red' },
    { hex: '#239530', name: 'Green', handle: 'pla-green' },
    { hex: '#0074D9', name: 'Blue', handle: 'pla-blue' }
  ],
  'pla-matte': [
    { hex: '#2A2A2A', name: 'Black', handle: 'pla-matte-black' },
    { hex: '#8C8C8C', name: 'Grey', handle: 'pla-matte-grey' },
    { hex: '#FAFAFA', name: 'White', handle: 'pla-matte-white' },
    { hex: '#E63946', name: 'Red', handle: 'pla-matte-red' },
    { hex: '#2A9D8F', name: 'Green', handle: 'pla-matte-green' },
    { hex: '#264653', name: 'Blue', handle: 'pla-matte-blue' }
  ],
  petg: [
    { hex: '#2A2A2A', name: 'Black', handle: 'petg-black' },
    { hex: '#FAFAFA', name: 'White', handle: 'petg-white' }
  ],
  abs: [
    { hex: '#2A2A2A', name: 'Black', handle: 'abs-black' },
    { hex: '#FAFAFA', name: 'White', handle: 'abs-white' }
  ]
};

const MAX_FILE_SIZE = 50 * 1024 * 1024;

// Material selection events
function initMaterialButtons() {
  document.querySelectorAll('.filament-type').forEach(el => {
    el.addEventListener('click', () => {
      document.querySelectorAll('.filament-type').forEach(btn => btn.classList.remove('active'));
      el.classList.add('active');
      const selectedMaterial = el.dataset.type;
      document.getElementById('selectedMaterial').value = selectedMaterial;
      
      updateColorSwatches(selectedMaterial);
      
      if (typeof mesh !== 'undefined' && mesh) {
        const currentColor = document.getElementById('customSelectedColor').value;
        mesh.material = createMaterial(selectedMaterial, currentColor);
      }
    });
  });
}

// Quality selection events
function initQualityButtons() {
  document.querySelectorAll('.quality-type').forEach(el => {
    el.addEventListener('click', () => {
      document.querySelectorAll('.quality-type').forEach(btn => btn.classList.remove('active'));
      el.classList.add('active');
      const selectedQuality = el.dataset.quality;
      document.getElementById('selectedQuality').value = selectedQuality;
    });
  });
}

// Support option events
function initSupportButtons() {
  document.querySelectorAll('.supports-type').forEach(el => {
    el.addEventListener('click', () => {
      document.querySelectorAll('.supports-type').forEach(btn => btn.classList.remove('active'));
      el.classList.add('active');
      const selectedSupports = el.dataset.supports;
      document.getElementById('selectedSupports').value = selectedSupports;
    });
  });
}

// Material appearance
const createMaterial = (materialType, color) => {
  const hexColor = color.replace('#', '0x');
  return new THREE.MeshStandardMaterial({ 
    color: parseInt(hexColor),
    flatShading: false,
    roughness: 0.7,
    metalness: 0.1,
    envMapIntensity: 0.5
  });
};

// Color swatch generation and selection
function updateColorSwatches(material) {
  const colorPicker = document.getElementById('color-picker');
  colorPicker.innerHTML = '';
  let firstInStockSwatchSet = false;
  
  colorOptions[material].forEach((color, index) => {
    const inventoryElement = document.querySelector(`[data-handle="${color.handle}"]`);
    
    if (inventoryElement) {
      const swatch = document.createElement('span');
      swatch.classList.add('color-swatch');
      
      const inStock = inventoryElement.dataset.available === 'true';
      
      if (!inStock) {
        swatch.classList.add('out-of-stock');
      } else if (!firstInStockSwatchSet) {
        swatch.classList.add('active');
        firstInStockSwatchSet = true;
        document.getElementById('customSelectedColor').value = color.hex;
        if (typeof mesh !== 'undefined' && mesh) {
          const hexColor = color.hex.replace('#', '0x');
          mesh.material.color.setHex(parseInt(hexColor));
        }
      }
      
      swatch.style.backgroundColor = color.hex;
      swatch.dataset.color = color.hex;
      swatch.dataset.colorName = color.name;
      swatch.title = `${color.name}${!inStock ? ' (Out of Stock)' : ''}`;
      
      if (inStock) {
        swatch.addEventListener('click', () => {
          document.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('active'));
          swatch.classList.add('active');
          document.getElementById('customSelectedColor').value = color.hex;
          if (typeof mesh !== 'undefined' && mesh) {
            const hexColor = color.hex.replace('#', '0x');
            mesh.material.color.setHex(parseInt(hexColor));
          }
        });
      }
      
      colorPicker.appendChild(swatch);
    }
  });
  
  if (!firstInStockSwatchSet) {
    document.getElementById('customSelectedColor').value = '';
    const submitButton = document.querySelector('.custom-submit-btn');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.title = 'No colours available for this material';
    }
  }
}

// Update infill slider visuals
function updateInfillSlider() {
  const infillSlider = document.getElementById('infill-percentage');
  const infillValue = document.getElementById('infill-value');
  
  const snappedValue = Math.round(infillSlider.value / 5) * 5;
  infillSlider.value = snappedValue;
  infillValue.textContent = `${snappedValue}%`;

  // Update slider appearance
  const percentage = (snappedValue - infillSlider.min) / (infillSlider.max - infillSlider.min) * 100;
  infillSlider.style.background = `linear-gradient(to right, #8BC34A 0%, #8BC34A ${percentage}%, #e0e0e0 ${percentage}%, #e0e0e0 100%)`;
}

// Infill slider functionality
function initInfillSlider() {
  const infillSlider = document.getElementById('infill-percentage');
  
  infillSlider.addEventListener('input', updateInfillSlider);
  updateInfillSlider();
}

// Reset form on discard - preserve selections
function initDiscardButton() {
  document.getElementById('discard-btn').addEventListener('click', function() {
    document.getElementById('results-view').style.display = 'none';
    
    // Don't reset the form, just make it visible again
    const form = document.getElementById('customMaterialForm');
    form.style.visibility = 'visible';
    
    const fileLabel = document.querySelector('.file-input-label');
    fileLabel.classList.remove('disabled');
    fileLabel.style.pointerEvents = 'auto';
    
    // Ensure infill slider visual is updated
    updateInfillSlider();
  });
}

// Initialize quantity selector
function initQuantitySelector() {
  const quantityInput = document.getElementById('item-quantity');
  const minusBtn = document.getElementById('quantity-minus');
  const plusBtn = document.getElementById('quantity-plus');
  
  minusBtn.addEventListener('click', () => {
    const currentValue = parseInt(quantityInput.value, 10);
    if (currentValue > 1) {
      quantityInput.value = currentValue - 1;
      updateTotalPrice();
    }
  });
  
  plusBtn.addEventListener('click', () => {
    const currentValue = parseInt(quantityInput.value, 10);
    quantityInput.value = currentValue + 1;
    updateTotalPrice();
  });
  
  quantityInput.addEventListener('change', () => {
    // Ensure quantity is at least 1
    if (parseInt(quantityInput.value, 10) < 1) {
      quantityInput.value = 1;
    }
    updateTotalPrice();
  });
}

// Update total price based on quantity
function updateTotalPrice() {
  const calculatedCostField = document.getElementById('calculated-cost');
  const quantityInput = document.getElementById('item-quantity');
  
  if (calculatedCostField && calculatedCostField.value && quantityInput) {
    const unitPrice = parseFloat(calculatedCostField.dataset.unitPrice || calculatedCostField.value.replace(/[^0-9.]/g, ''));
    const quantity = parseInt(quantityInput.value, 10);
    
    if (!isNaN(unitPrice) && !isNaN(quantity)) {
      const totalPrice = (unitPrice * quantity).toFixed(2);
      calculatedCostField.value = `$${totalPrice}`;
      
      // Store the unit price for future calculations
      calculatedCostField.dataset.unitPrice = unitPrice;
    }
  }
}

// Support sensitivity backend configuration
// This allows programmatic adjustment of support detection sensitivity
function setSupportSensitivity(value) {
  // Apply the sensitivity value to the analyzer if available
  if (typeof STLWeightEstimator !== 'undefined') {
    return STLWeightEstimator.setSupportSensitivity(value);
  }
  return false;
}

// Get current support sensitivity value
function getSupportSensitivity() {
  if (typeof STLWeightEstimator !== 'undefined') {
    return STLWeightEstimator.supportSensitivity;
  }
  return 5; // Default value
}

// Initialize all UI components
function initUI() {
  initMaterialButtons();
  initQualityButtons();
  initSupportButtons();
  initInfillSlider();
  initDiscardButton();
  initQuantitySelector();
  
  // Set initial color swatches
  updateColorSwatches('pla');
}

// Initialize UI when DOM content is loaded
document.addEventListener('DOMContentLoaded', initUI); 